import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  describeViolationField,
  type GoogleValidationResult,
  type GoogleViolation,
} from '../../lib/validateGoogleContent';

// Google Validation Errors modal. Renders the violation set produced
// by validateGoogleContent, grouped by location. Each violation is a
// clickable row that navigates to the Google Build View for that row,
// preserving filteredRowIds in location.state when available so
// Prev/Next still walks the same set the AM was looking at.
//
// "All clear ✓" state when result.violations.length === 0 — same
// modal opens; user gets reassurance that the export is safe.
//
// Dismissed via Cancel button, ESC, or backdrop click. Default focus
// on Close so muscle-memory Enter doesn't accidentally trigger
// navigation by hitting the first violation row.

type Props = {
  rolloutId: string;
  result: GoogleValidationResult;
  // Optional. When supplied, navigation carries this in location.state
  // so Build View's Prev/Next stays meaningful. Missing → Build View
  // disables Prev/Next; row content still loads.
  filteredRowIds?: string[] | null;
  onClose: () => void;
};

export function GoogleErrorsModal({
  rolloutId,
  result,
  filteredRowIds,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isClean = result.violations.length === 0;
  const handleNavigate = (v: GoogleViolation) => {
    onClose();
    navigate(`/rollouts/${rolloutId}/grid/google/${v.rowId}/build`, {
      state:
        filteredRowIds && filteredRowIds.length > 0
          ? { filteredRowIds }
          : undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={() => onClose()}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-errors-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-4 border-b border-slate-200">
          <h2
            id="google-errors-title"
            className="text-lg font-semibold text-slate-900"
          >
            Google Validation Errors
          </h2>
          {isClean ? (
            <p className="text-sm text-emerald-700 mt-1">
              All clear ✓ — every Google content field is within Google Ads' length limits.
            </p>
          ) : (
            <p className="text-sm text-red-700 mt-1">
              {result.violations.length} {result.violations.length === 1 ? 'error' : 'errors'}
              {' '}across{' '}
              {result.locationCount} {result.locationCount === 1 ? 'location' : 'locations'}.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-5">
          {isClean ? (
            <p className="text-sm text-slate-600">
              No fields exceed their character limits. Safe to export.
            </p>
          ) : (
            [...result.violationsByAccount.entries()].map(
              ([accountId, accountViolations]) => (
                <ViolationGroup
                  key={accountId}
                  violations={accountViolations}
                  onSelect={handleNavigate}
                />
              ),
            )
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end">
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ViolationGroup({
  violations,
  onSelect,
}: {
  violations: GoogleViolation[];
  onSelect: (v: GoogleViolation) => void;
}) {
  // Every violation in a group shares one accountId + locationName
  // (the validator builds the bucket that way). Read it from the
  // first entry rather than threading the location label down.
  const first = violations[0];
  if (!first) return null;
  const locationName = first.locationName;
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        <span aria-hidden className="inline-block w-2 h-2 rounded-full bg-red-500" />
        {locationName}
      </h3>
      <ul className="mt-2 space-y-2 pl-4">
        {violations.map((v, idx) => (
          <li key={`${v.rowId}-${v.fieldType}-${v.fieldIndex ?? 'x'}-${idx}`}>
            <button
              type="button"
              onClick={() => onSelect(v)}
              className="w-full text-left rounded border border-red-200 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-400 px-3 py-2"
            >
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-slate-900">
                  {describeViolationField(v)}
                </span>
                <span className="text-xs text-red-700 whitespace-nowrap">
                  {v.charCount} chars (max {v.limit})
                </span>
              </div>
              <div
                className="text-xs font-mono text-slate-700 mt-1 break-words"
                title={v.resolvedValue}
              >
                {v.resolvedValue}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
